// src/components/SinglePost.js

import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext"; // Import NavigationContext
import { fetchPost, deletePost, createComment } from "../services/postService";
import { formatDistance, format } from "date-fns";
import { sl } from "date-fns/locale";
import PostFormModal from "./PostFormModal";
import UserAvatar from "../components/UserAvatar";
import "../components/CommunityPosts.css"; // Import the shared CSS

function SinglePost() {
  const { postId } = useParams();
  const { user } = useAuth();
  const { isMobile } = useNavigation(); // Get mobile status
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Post edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  
  useEffect(() => {
    loadPost();
  }, [postId]);
  
  const loadPost = async () => {
    setIsLoading(true);
    try {
      const postData = await fetchPost(postId);
      setPost(postData);
      setComments(postData.comments || []);
    } catch (err) {
      setError("Napaka pri nalaganju objave.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to format date as "X time ago" in Slovenian
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return formatDistance(date, new Date(), { addSuffix: true, locale: sl });
  };
  
  // Function to format full date
  const formatFullDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return format(date, "d. MMMM yyyy 'ob' HH:mm", { locale: sl });
  };
  
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentText.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      const result = await createComment({
        content: commentText,
        content_type: "post",
        content_id: post.id
      });
      
      // Update the comments list with the new comment
      if (result && result.comment) {
        setComments([...comments, result.comment]);
        setCommentText("");
      }
    } catch (err) {
      setError("Napaka pri objavi komentarja.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeletePost = async () => {
    try {
      await deletePost(postId);
      navigate("/posts");
    } catch (err) {
      setError("Napaka pri brisanju objave.");
      setShowDeleteModal(false);
    }
  };
  
  // Modal functions
  const openEditModal = () => setShowEditModal(true);
  const closeEditModal = () => setShowEditModal(false);
  
  // Handle post update
  const handlePostUpdated = (updatedPost) => {
    setPost(updatedPost);
  };
  
  // Check if current user is author or admin and can edit/delete
  const canModify = post && (
    post.user_id === user?.id || 
    user?.role === "community_admin" || 
    user?.role === "global_admin"
  );
  
  // Create post actions dropdown
  const renderPostActions = () => {
    if (!canModify) return null;
    
    return (
      <div className="dropdown">
        <button
          className="btn btn-sm btn-link text-muted"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i className="bi bi-three-dots-vertical"></i>
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          <li>
            <button 
              className="dropdown-item"
              onClick={openEditModal}
            >
              <i className="bi bi-pencil me-2"></i> Uredi
            </button>
          </li>
          <li>
            <button 
              className="dropdown-item text-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              <i className="bi bi-trash me-2"></i> Izbriši
            </button>
          </li>
        </ul>
      </div>
    );
  };
  
  if (isLoading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/posts" className="btn btn-primary">
          Nazaj na objave
        </Link>
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning" role="alert">
          Objava ne obstaja ali pa je bila izbrisana.
        </div>
        <Link to="/posts" className="btn btn-primary">
          Nazaj na objave
        </Link>
      </div>
    );
  }
  
  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          {/* Post content */}
          <div className="card shadow-sm border-0 rounded-4 mb-4">
            <div className="card-body p-4">
              {isMobile ? (
                // Mobile-friendly chat-like display for post author
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    {/* Avatar */}
                    <UserAvatar
                      profileImage={post.profile_image}
                      displayName={post.display_name}
                      email={post.email}
                      size={32}
                      className="me-2"
                      avatarOnly={true} // Only show the avatar
                    />
                    <div className="d-flex flex-column flex-grow-1">
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="fw-semibold text-truncate" style={{maxWidth: '180px'}}>
                          {post.display_name || post.email?.split('@')[0]}
                        </span>
                        {renderPostActions()}
                      </div>
                      <small className="text-muted x-small">
                        {formatFullDate(post.created_at)}
                      </small>
                    </div>
                  </div>
                </div>
              ) : (
                // Desktop version
                <UserAvatar
                  profileImage={post.profile_image}
                  displayName={post.display_name}
                  email={post.email}
                  timestamp={post.created_at}
                  timestampFormat={formatFullDate(post.created_at)}
                  size={48}
                  actions={renderPostActions()}
                  className="mb-3"
                />
              )}
              
              {post.title && <h3 className={`mb-3 ${isMobile ? 'fs-4' : ''}`}>{post.title}</h3>}
              
              <div className="post-content mb-4">
                {post.content.split('\n').map((paragraph, index) => (
                  paragraph ? <p key={index}>{paragraph}</p> : <br key={index} />
                ))}
              </div>
              
              <div className="border-top pt-3">
                <span className="text-muted">
                  <i className="bi bi-chat-text me-1"></i> {comments.length} komentarjev
                </span>
              </div>
            </div>
          </div>
          
          {/* Comments section */}
          <div className="card shadow-sm border-0 rounded-4 mb-4">
            <div className="card-header bg-transparent">
              <h5 className="mb-0">Komentarji</h5>
            </div>
            <div className="card-body p-4">
              {comments.length > 0 ? (
                <div className="comments-list mb-4">
                  {comments.map(comment => (
                    <div key={comment.id} className="comment mb-3 pb-3 border-bottom">
                      {isMobile ? (
                        // Mobile-friendly chat-like comment
                        <div>
                          <div className="d-flex align-items-center mb-1">
                            {/* Avatar */}
                            <UserAvatar
                              profileImage={comment.profile_image}
                              displayName={comment.display_name}
                              email={comment.email}
                              size={24}
                              className="me-2"
                              avatarOnly={true} // Only show the avatar
                            />
                            <div className="d-flex justify-content-between align-items-baseline w-100">
                              <span className="fw-semibold me-2 text-truncate" style={{maxWidth: '120px'}}>
                                {comment.display_name || comment.email?.split('@')[0]}
                              </span>
                              <small className="text-muted x-small timestamp-small">
                                {formatTimeAgo(comment.created_at)}
                              </small>
                            </div>
                          </div>
                          <p className="mb-0 ms-4 message-content">{comment.content}</p>
                        </div>
                      ) : (
                        // Desktop version
                        <>
                          <UserAvatar
                            profileImage={comment.profile_image}
                            displayName={comment.display_name}
                            email={comment.email}
                            timestamp={comment.created_at}
                            timestampFormat={formatTimeAgo(comment.created_at)}
                            size={32}
                          />
                          <p className="mb-0 mt-2 ms-4">{comment.content}</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center py-3">
                  Ni komentarjev. Bodi prvi!
                </p>
              )}
              
              {/* Comment form */}
              <form onSubmit={handleCommentSubmit}>
                <div className="mb-3">
                  <label htmlFor="commentText" className="form-label">
                    Dodaj komentar
                  </label>
                  <textarea
                    className="form-control"
                    id="commentText"
                    rows={isMobile ? "2" : "3"}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Napiši komentar..."
                    required
                  ></textarea>
                </div>
                <div className="text-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || !commentText.trim()}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Objavljanje...
                      </>
                    ) : (
                      "Objavi komentar"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/posts" className="btn btn-outline-primary">
              Nazaj na vse objave
            </Link>
          </div>
        </div>
      </div>
      
      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Potrdi brisanje</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Ali res želiš izbrisati to objavo? Tega dejanja ni mogoče razveljaviti.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Prekliči
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeletePost}
                >
                  Izbriši
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Post Edit Modal */}
      <PostFormModal 
        show={showEditModal}
        handleClose={closeEditModal}
        postId={postId}
        onPostSaved={handlePostUpdated}
      />
    </div>
  );
}

export default SinglePost;